/**
 * lib/branding.ts — ChatSyncs single source of truth for brand identity.
 *
 * White-label steps (if adapting for a client):
 *   1. Update logo.imageSrc and logo.alt
 *   2. Update brandName and tagline
 *   3. Update metadata (title, description, favicon)
 *   4. Replace PRIMARY_PALETTE with client's color scale
 *      → primary, primaryHover, primaryMuted are derived automatically (600, 700, 100)
 *   5. Update optional URLs and support contact (termsOfServiceUrl, privacyPolicyUrl, supportEmail)
 *   6. Update businessOwnerName (proprietor / business contact shown in footer copyright)
 *   7. Update business (legal name, public address) — name must match the GST certificate
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LogoType = 'svg' | 'image'

export type PrimaryColorPalette = Record<
  '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900',
  string
>

export interface LogoConfig {
  type: LogoType
  imageSrc?: string
  alt: string
  backgroundColor?: string
  svgViewBox?: string
  svgPath?: string // space-separated polygon/polyline points for SVG
}

export interface MetadataConfig {
  title: string
  description: string
  favicon?: string
}

export interface AnnouncementBannerConfig {
  enabled: boolean
  message: string
  ctaLabel: string
  ctaHref: string
}

export interface SemanticColors {
  mediumPrimary: string   // Medium Blue (brand)
  primary: string         // Main brand color — CTAs, links, highlights
  primaryHover: string    // Darker shade for hover states
  primaryMuted: string    // Light tint for backgrounds, badges, subtle fills
  background: string      // Page background
  surface: string         // Card / panel surface
  surfaceElevated: string // Elevated surface (modals, dropdowns)
  text: string            // Primary body text
  textMuted: string       // Secondary / subdued text
  border: string          // Dividers and borders
  success: string         // Success / positive states
  warning: string         // Warning / caution states
  error: string           // Error / destructive states
  whatsapp: string        // WhatsApp Green — use ONLY for WhatsApp-specific UI elements
  /** Meta Business Partner badge (footer / dark pill): background, border, text */
  metaBadgeBg: string
  metaBadgeBorder: string
  metaBadgeText: string
}

/** One tier's color identity — a saturated accent for badges/icons and a muted tint for backgrounds/fills. */
export interface TierColorPair {
  accent: string
  muted: string
}

/**
 * Data-visualisation roles for admin charts. Every value is an existing brand color (no new hex):
 * validated with the dataviz palette checker (CVD separation, chroma, lightness band) on white.
 * `series3` sits below 3:1 contrast on white, so charts using it must also show labels or a table.
 */
export interface ChartColors {
  /** Categorical slots, in fixed order (never cycled). */
  series1: string
  series2: string
  series3: string
  /** Totals, baselines and comparison lines. */
  neutral: string
  /** MRR gained (new, reactivation, expansion). */
  gain: string
  /** MRR lost (churned, contraction). */
  loss: string
  /** Gridlines and axes: recessive. */
  grid: string
}

/** Tones for status chips in the admin (plan, stage, payment state…). Each pairs a pastel fill with a readable ink. */
export type AdminChipTone = 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'orange'

/**
 * Admin panel identity: a light-only, Attio-style app UI (dense tables, hairline borders,
 * near-white sidebar). Separate from the marketing tokens because an app screen needs more
 * neutral steps and quieter depth than a landing page. Every chip ink has ≥ 4.5:1 contrast on its fill.
 */
export interface AdminTheme {
  colors: {
    canvas: string       // main content area
    subtle: string       // sidebar, table header, toolbar strips
    hover: string        // row / nav item hover
    selected: string     // active nav item, selected row
    line: string         // hairline borders and dividers
    lineStrong: string   // input borders, emphasised dividers
    ink: string          // primary text
    inkMuted: string     // secondary text, labels
    inkFaint: string     // icons, placeholders, disabled text (not for body copy: ~3.2:1 on white)
  }
  chips: Record<AdminChipTone, { fill: string; ink: string }>
  /** Depth is mostly borders; shadows only lift things that float. */
  shadows: {
    card: string      // resting surfaces (cards, tables)
    button: string    // secondary buttons
    float: string     // popovers, menus, tooltips
    modal: string     // dialogs and side panels
  }
  /** App type scale: 12 / 13 / 14 / 16 / 20 px. Body copy in the admin is `sm` or `base`. */
  fontSize: { xs: string; sm: string; base: string; lg: string; xl: string }
}

/** Partner commission-ladder tier colors (Bronze/Silver/Gold/Platinum) — see constants/commission.ts for the tier thresholds themselves. */
export interface TierColors {
  bronze: TierColorPair
  silver: TierColorPair
  gold: TierColorPair
  platinum: TierColorPair
}

/** Semantic font size tokens — single source of truth for consistent typography across the site. */
export type TypographyScale = {
  caption: string       // 12px — badges, nav labels, small labels
  body: string         // 15px — main body text, descriptions, footer links
  bodyLarge: string    // 16px — emphasized body, section subtitles
  lead: string        // 18px — hero intro, intro paragraphs
  headingCard: string // 15px — card titles (feature)
  headingCardLg: string // 18px — larger card titles (pricing tier)
  headingSubsection: string   // 20px — subsection titles (HowItWorks steps, stats)
  headingSection: string      // 26px — section H2 base
  headingSectionMd: string    // 30px — section H2 at md
  headingSectionLg: string   // 40px — section H2 at lg
  headingHero: string       // 30px — hero H1 base
  headingHeroMd: string     // 36px — hero H1 at md
  headingHeroLg: string     // 48px — hero H1 at lg
  headingPage: string       // 30px — page title (legal, FinalCTA) base
  headingPageMd: string     // 36px — page title at md
}

/**
 * WhatsApp contact config — single source of truth for all wa.me links.
 * Change `number` or any `messages.*` key here and every button on the site updates.
 */
export interface WhatsAppConfig {
  /** E.164 format without the '+' — used to build wa.me URLs. */
  number: string
  /** Pre-built wa.me URLs for each context. */
  urls: {
    /** Generic enquiry — used in blog, pricing FAQ, navbar, etc. */
    general: string
    /** Free Onboarding enquiry */
    dedicatedSupport: string
    /** Free support / doubt-clearing call booking */
    freeSupport: string
    /** Custom Solution enquiry */
    customSolution: string
    /** Expert Services hero CTA */
    expertServices: string
    /** Pricing plan help */
    pricingHelp: string
    /** Partner Program enquiry */
    partnerProgram: string
  }
  /** Button / link labels for each context. */
  labels: {
    general: string
    serviceCard: string
    expertServicesHero: string
    diySection: string
  }
}

/** One external social profile — used in the footer social icons and JSON-LD `sameAs`. */
export interface SocialProfile {
  label: string
  href: string
  ariaLabel: string
}

/** Registered business identity — legal name as on the GST certificate, plus the public address. */
export interface BusinessIdentity {
  legalName: string        // Proprietor's full legal name
  address: string          // Public address shown in the site footer (city level only)
}

export interface BrandingConfig {
  logo: LogoConfig
  brandName: string
  copyrightHolder: string   // Name used in "© Year Name. All rights reserved." (often same as businessOwnerName)
  /** Proprietor or business contact name — shown in footer copyright next to the brand. */
  businessOwnerName: string
  /**
   * Registered business identity, named as on the GST certificate. Shown in the site footers so
   * Meta business verification can match chatsyncs.com to the documents.
   */
  business: BusinessIdentity
  tagline: string
  contactAddress: string     // Primary business address shown in footer
  contactEmail: string       // Primary contact email shown in footer
  contactPhone: string       // Primary contact phone number shown in footer
  metadata: MetadataConfig
  /** Controls visibility of sign-up / free-trial CTA buttons across the site. */
  hideSignupButtons: boolean
  /**
   * When true, pricing plans and the application form are locked — plans are visible
   * but CTAs are replaced with a community link. Set to false to re-enable purchases.
   */
  plansLocked: boolean
  /** WhatsApp community link shown when plansLocked is true. */
  communityUrl: string
  /** Direct WhatsApp contact — all wa.me links and button labels live here. */
  whatsApp: WhatsAppConfig
  /** External social profiles — single source for footer icons and JSON-LD `sameAs`. WhatsApp is intentionally excluded (see whatsApp.urls.general). */
  socialProfiles: SocialProfile[]
  announcementBanner: AnnouncementBannerConfig
  theme: 'light' | 'dark'
  typography: {
    fontFamily: string         // Body font (CSS font-family string)
    fontFamilyHeading: string  // Heading font (CSS font-family string)
    fontFamilyMono: string     // Monospace font
    fontSize: TypographyScale  // Semantic font sizes — use text-caption, text-body, etc.
    /**
     * A denser variant of `fontSize`, one step down across the board — for
     * layouts that need to fit more content in less space (e.g. a 4-column
     * pricing grid) without introducing a one-off, page-named scale. Use
     * text-compact-caption, text-compact-heading-card-lg, etc. (see
     * app/globals.css's `--text-compact-*` vars, kept in sync with this).
     */
    fontSizeCompact: TypographyScale
  }
  colors: { primaryPalette: PrimaryColorPalette } & SemanticColors
  /** Partner commission-tier identity — badges, progress bars, discount-level states. */
  tierColors: TierColors
  /** Admin chart roles, derived from the palette and tier colors above. */
  chartColors: ChartColors
  /** Admin panel app UI (light only). Mirrored in app/globals.css as --color-admin-*, --color-ink*, --color-chip-*, --shadow-admin-* and --text-admin-*. */
  admin: AdminTheme
  /** Marketing site root — use for sitemap, robots, canonical, Open Graph. Must match GSC property (e.g. https://chatsyncs.com or https://www.chatsyncs.com). */
  siteUrl: string
  /** Developer site root (Mintlify): Developer API (/developer-api) and MCP docs (/mcp). Replaces docs.chatsyncs.com, now redirect-only. */
  developerUrl: string
  /** Help Center root: customer guides (/docs), video lessons (/learn), support tickets (/support). */
  helpUrl: string
  /**
   * Deep links into the guides, keyed by feature-page slug (constants/featurePages.ts).
   * The feature-page template renders a "Step-by-step guide" band from it; a missing
   * key means no band renders. human-support and click-to-whatsapp-ads have no entry
   * on purpose — no guide matches those pages (Lakshit, 2026-10-15).
   * Guides live in the Help Center (helpUrl); the catalog, payments and REST API pages
   * point at the Developer API (developerUrl). Checked against the Help Center's page list on
   * 2026-10-15; if a page is renamed or moved, update it here, not in a component.
   */
  docsLinks: Record<string, string>
  appUrl: string                // App root (fallback)
  appLoginUrl: string           // App login screen
  appSignupUrl: string          // App signup / trial screen
  appAccountUrl: string         // App account/settings screen (post-purchase)
  termsOfServiceUrl?: string
  privacyPolicyUrl?: string
  refundPolicyUrl?: string
  supportEmail: string          // Single source of truth for support contact
  supportUrl?: string           // mailto: link (derived from supportEmail)
  salesEmail: string            // Sales / "Talk to Sales" / "Book a Migration Call"
  salesUrl?: string             // mailto: link (derived from salesEmail)
  /** Webhook URL for pricing contact form submissions (n8n). */
  pricingFormWebhookUrl?: string
  /** Cal.com booking URL for Managed plan strategy calls. */
  calComUrl: string
  /** Cal.com booking URL for the Partner Program enquiry call. */
  partnerProgramCalUrl: string
  /**
   * Plan & add-on pricing — single source of truth for all displayed prices.
   * Change a value here and every page (pricing, compare, FAQs) updates.
   * Amounts are raw monthly INR (no symbol, no formatting).
   */
  pricing: {
    starterMonthlyINR: number
    proMonthlyINR: number
    /** Additional WhatsApp Number (WABA) add-on, per number, one-time purchase, INR. */
    additionalWhatsAppNumberINR: number
  }
  /**
   * Platform checkout URLs — one per plan × billing cycle.
   * Format: https://platform.chatsyncs.com/payment/buy-package/{packageId}
   */
  monthlyStarterPurchaseUrl: string
  monthlyProPurchaseUrl: string
  annualStarterPurchaseUrl: string
  annualProPurchaseUrl: string
  /**
   * Self-serve subscription page — where every quantity add-on
   * (extra WhatsApp numbers, extra team members) sends the customer.
   */
  subscriptionUrl: string
}

// ---------------------------------------------------------------------------
// Primary palette — replace this block to re-theme the entire product.
// primary       = palette['600']  → #2B7DE9  (Blue — brand primary)
// primaryHover  = palette['700']  → #0B3D8C  (Dark Blue — brand)
// primaryMuted  = palette['100']  → #DBEAFE  (Light blue tint)
// ---------------------------------------------------------------------------
const PRIMARY_PALETTE: PrimaryColorPalette = {
  '50':  '#EFF6FF',
  '100': '#DBEAFE', // primaryMuted
  '200': '#BFDBFE',
  '300': '#93C5FD',
  '400': '#4A9AF5', // Medium Blue (brand)
  '500': '#3B82F6',
  '600': '#2B7DE9', // primary — Blue (brand)
  '700': '#0B3D8C', // primaryHover — Dark Blue (brand)
  '800': '#1E3A5F',
  '900': '#0B1120', // Deep Navy (brand)
}

// ---------------------------------------------------------------------------
// Brand name — single source of truth used across the entire website
// ---------------------------------------------------------------------------
const BRAND_NAME = 'ChatSyncs'

// Border is named here so chartColors.grid reuses it without repeating the hex value.
const BORDER_COLOR = '#E6ECF5' // subtle light blue-grey

// Tier colors are named here so chartColors can reuse them without repeating a hex value.
const TIER_COLORS: TierColors = {
  bronze:   { accent: '#B45D3C', muted: '#FBEAE3' }, // warm terracotta — entry tier
  silver:   { accent: '#64748B', muted: '#EEF1F5' }, // cool slate — brand-neutral
  gold:     { accent: '#D9910A', muted: '#FDF1DC' }, // brand warning-amber, earned
  platinum: { accent: '#2B7DE9', muted: '#DBEAFE' }, // brand primary blue — the house color, top tier
}

// Text colors are named here so the admin theme reuses them without repeating a hex value.
const TEXT_COLOR = '#0B1120'       // Deep Navy (brand)
const TEXT_MUTED_COLOR = '#475569' // darker slate

// Admin panel neutrals: the brand's blue-grey, stepped for an app UI (Attio-style).
const ADMIN_THEME: AdminTheme = {
  colors: {
    canvas:     '#FFFFFF',
    subtle:     '#F7F9FC',
    hover:      '#F0F3F8',
    selected:   '#E8EEF7',
    line:       BORDER_COLOR,
    lineStrong: '#D5DDE8',
    ink:        TEXT_COLOR,
    inkMuted:   TEXT_MUTED_COLOR,
    inkFaint:   '#8492A6',
  },
  chips: {
    neutral: { fill: TIER_COLORS.silver.muted,   ink: TEXT_MUTED_COLOR },
    blue:    { fill: PRIMARY_PALETTE['100'],     ink: PRIMARY_PALETTE['700'] },
    green:   { fill: '#E3F6F3',                  ink: '#0F766E' }, // from success teal
    amber:   { fill: TIER_COLORS.gold.muted,     ink: '#8A5A06' }, // from warning amber
    red:     { fill: '#FDECEC',                  ink: '#B42318' }, // from error red
    orange:  { fill: TIER_COLORS.bronze.muted,   ink: '#9A4A2C' }, // from bronze
  },
  // Deep Navy (#0B1120) at low opacity, so shadows stay on-brand.
  shadows: {
    card:   '0 1px 2px rgba(11,17,32,0.04)',
    button: '0 1px 2px rgba(11,17,32,0.06)',
    float:  '0 0 0 1px rgba(11,17,32,0.04), 0 4px 8px -2px rgba(11,17,32,0.06), 0 12px 32px -8px rgba(11,17,32,0.14)',
    modal:  '0 0 0 1px rgba(11,17,32,0.04), 0 24px 64px -16px rgba(11,17,32,0.28)',
  },
  fontSize: {
    xs:   '0.75rem',   // 12px — chips, column labels, meta
    sm:   '0.8125rem', // 13px — table cells, inputs, nav
    base: '0.875rem',  // 14px — body copy, buttons
    lg:   '1rem',      // 16px — section titles
    xl:   '1.25rem',   // 20px — page titles
  },
}

// ---------------------------------------------------------------------------
// Business owner / copyright — single source for footer © line and legal name
// ---------------------------------------------------------------------------
const BUSINESS_OWNER_NAME = 'Lakshit Ukani'

// ---------------------------------------------------------------------------
// Registered business — legal name must match the GST certificate (GSTIN 27AEXPU5407L2ZJ).
// Meta business verification compares it against the documents. The site shows only the city-level
// address, never the full registered address.
// ---------------------------------------------------------------------------
const BUSINESS_LEGAL_NAME = 'Lakshit Ishwar Ukani'
const BUSINESS_ADDRESS = 'Mumbai, Maharashtra 401107, India'

// ---------------------------------------------------------------------------
// Support contact — single source of truth for email across the site
// ---------------------------------------------------------------------------
const SUPPORT_EMAIL = 'support@chatsyncs.com'

// ---------------------------------------------------------------------------
// Help Center — guides, video lessons, support tickets, Chatty
// ---------------------------------------------------------------------------
const HELP_URL = 'https://help.chatsyncs.com'

// ---------------------------------------------------------------------------
// Developer site — Developer API and MCP docs (Mintlify). Link here directly:
// docs.chatsyncs.com only redirects now.
// ---------------------------------------------------------------------------
const DEVELOPER_URL = 'https://developer.chatsyncs.com'

// ---------------------------------------------------------------------------
// Sales contact — "Talk to Sales", "Book a Migration Call", Enterprise CTA
// ---------------------------------------------------------------------------
const SALES_EMAIL = 'support@chatsyncs.com'

// ---------------------------------------------------------------------------
// Primary contact details — used in marketing site footer
// ---------------------------------------------------------------------------
const CONTACT_ADDRESS =
  'Mumbai, Maharashtra 401107'
const CONTACT_EMAIL = 'support@chatsyncs.com'
const CONTACT_PHONE = '+91 9326268231'

// ---------------------------------------------------------------------------
// WhatsApp direct contact — single source for all wa.me links across the site.
// To update any message or the number, change it here only.
// ---------------------------------------------------------------------------
const WA_NUMBER = '919326268231'
const wa = (msg: string) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`

const WHATSAPP_CONFIG: WhatsAppConfig = {
  number: WA_NUMBER,
  urls: {
    general:             wa('Hi, I have a question about ChatSyncs.'),
    dedicatedSupport:    wa("Hi, I'd like help with the free ChatSyncs onboarding."),
    freeSupport:         wa("Hi! I'd like to book a free support call to clear a doubt about ChatSyncs."),
    customSolution:      wa("Hi, I'm interested in a Custom Solution."),
    expertServices:      wa("Hi, I'm interested in your Expert Services."),
    pricingHelp:         wa('Hi, I need help choosing a plan.'),
    partnerProgram:      wa('Hi, I have a question about the ChatSyncs Partner Program.'),
  },
  labels: {
    general:             'Chat on WhatsApp',
    serviceCard:         'Chat on WhatsApp',
    expertServicesHero:  'Chat on WhatsApp',
    diySection:          'Chat on WhatsApp — Expert Services',
  },
}

// ---------------------------------------------------------------------------
// BRANDING — ChatSyncs
// ---------------------------------------------------------------------------
/** Reference palettes used only inside created third-party product demonstrations.
 * Keep app surfaces recognisable; these do not change ChatSyncs site branding. */
export const MCP_CLIENT_UI = {
  chatgpt: { bg: '#212121', sidebar: '#171717', panel: '#303030', text: '#ECECEC', muted: '#B4B4B4', line: '#444444', accent: '#FFFFFF' },
  claude: { bg: '#FAF9F5', sidebar: '#F0EEE6', panel: '#EAE7DE', text: '#30302E', muted: '#66645E', line: '#D4D1C8', accent: '#C15F3C' },
  'claude-code': { bg: '#181818', sidebar: '#222222', panel: '#292929', text: '#E8E6E0', muted: '#A8A69E', line: '#55534D', accent: '#E39172' },
  'grok-bot': { bg: '#101010', sidebar: '#181818', panel: '#282828', text: '#F4F4F4', muted: '#B0B0B0', line: '#393939', accent: '#FFFFFF' },
  hermes: { bg: '#0E1621', sidebar: '#17212B', panel: '#202E3D', text: '#F5F5F5', muted: '#A7B8C8', line: '#34485B', accent: '#7DC3F5' },
  openclaw: { bg: '#12141A', sidebar: '#191C24', panel: '#232833', text: '#F0F2F7', muted: '#ABB2C1', line: '#3B414F', accent: '#FF8B84' },
  cursor: { bg: '#181818', sidebar: '#202020', panel: '#2B2B2B', text: '#E6E6E6', muted: '#AFAFAF', line: '#404040', accent: '#E6E6E6' },
  gemini: { bg: '#171B24', sidebar: '#212735', panel: '#262F40', text: '#E6EAF3', muted: '#ADB9D0', line: '#47536B', accent: '#9BBEFF' },
  vscode: { bg: '#1F1F1F', sidebar: '#181818', panel: '#292929', text: '#E0E0E0', muted: '#B4B4B4', line: '#454545', accent: '#76BBFF' },
  codex: { bg: '#FFFFFF', sidebar: '#F5F5F5', panel: '#F0F0F0', text: '#232323', muted: '#666666', line: '#D8D8D8', accent: '#232323' },
} as const

export const BRANDING: BrandingConfig = {
  brandName: BRAND_NAME,
  copyrightHolder: BUSINESS_OWNER_NAME,
  businessOwnerName: BUSINESS_OWNER_NAME,
  business: {
    legalName: BUSINESS_LEGAL_NAME,
    address: BUSINESS_ADDRESS,
  },
  tagline: 'More Leads. Faster Follow-Up. More Revenue.',
  contactAddress: CONTACT_ADDRESS,
  contactEmail: CONTACT_EMAIL,
  contactPhone: CONTACT_PHONE,

  logo: {
    type: 'image',
    imageSrc: '/logo.png', // source: assets/logo.png → copy to public/logo.png
    alt: BRAND_NAME,
  },

  metadata: {
    title: `${BRAND_NAME} — More Leads. Faster Follow-Up. More Revenue.`,
    description:
      `${BRAND_NAME} helps growing businesses respond instantly, follow up automatically, and close more sales on WhatsApp. Set it up yourself or let our team do everything for you.`,
    favicon: '/favicon.ico',
  },
  hideSignupButtons: false,
  plansLocked: false,
  communityUrl: 'https://chat.whatsapp.com/LEBdJrxr5LVLjJGR1w916j',
  whatsApp: WHATSAPP_CONFIG,
  socialProfiles: [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/chatsyncs/', ariaLabel: 'LinkedIn' },
    { label: 'YouTube', href: 'https://www.youtube.com/@lakshit-ukani', ariaLabel: 'YouTube' },
    { label: 'Instagram', href: 'https://www.instagram.com/chatsyncs/', ariaLabel: 'Instagram' },
  ],
  announcementBanner: {
    enabled: true,
    message: 'AI Agents are live. Turn WhatsApp conversations into sales & support, 24/7.',
    ctaLabel: 'Explore Now',
    ctaHref: '/ai-agents',
  },

  theme: 'light',

  typography: {
    // Inter — single brand typeface for headings + body. Heavy weights (800/900) with
    // tight negative tracking give the crisp, confident look of best-in-class SaaS sites.
    fontFamilyHeading: "'Inter', system-ui, -apple-system, sans-serif",
    fontFamily:        "'Inter', system-ui, -apple-system, sans-serif",
    fontFamilyMono:    "'JetBrains Mono', monospace",
    fontSize: {
      caption:           '0.75rem',   // 12px — badges, labels
      body:              '0.9375rem', // 15px — main body
      bodyLarge:         '1rem',      // 16px — emphasized body
      lead:              '1.125rem',  // 18px — hero intro
      headingCard:       '0.9375rem', // 15px — card titles
      headingCardLg:     '1.125rem',  // 18px — pricing tier name
      headingSubsection: '1.25rem',   // 20px — subsection titles
      headingSection:    '1.625rem',  // 26px — section H2 base
      headingSectionMd:  '1.875rem',  // 30px — section H2 md
      headingSectionLg:  '2.5rem',    // 40px — section H2 lg
      headingHero:       '1.875rem',  // 30px — hero H1 base
      headingHeroMd:     '2.25rem',   // 36px — hero H1 md
      headingHeroLg:     '3rem',      // 48px — hero H1 lg
      headingPage:       '1.875rem',  // 30px — page title base
      headingPageMd:     '2.25rem',   // 36px — page title md
    },
    // Compact scale — one step down from fontSize across the board. Mirrored
    // in app/globals.css's `--text-compact-*` vars (Tailwind text-compact-*
    // classes) — keep both in sync when adjusting.
    fontSizeCompact: {
      caption:           '0.6875rem', // 11px
      body:              '0.8125rem', // 13px
      bodyLarge:         '0.875rem',  // 14px
      lead:              '1rem',      // 16px
      headingCard:       '0.8125rem', // 13px
      headingCardLg:     '1rem',      // 16px — pricing tier name
      headingSubsection: '1.125rem',  // 18px
      headingSection:    '1.375rem',  // 22px
      headingSectionMd:  '1.625rem',  // 26px
      headingSectionLg:  '2.125rem',  // 34px
      headingHero:       '1.625rem',  // 26px
      headingHeroMd:     '1.875rem',  // 30px
      headingHeroLg:     '2.5rem',    // 40px
      headingPage:       '1.625rem',  // 26px
      headingPageMd:     '1.875rem',  // 30px
    },
  },

  colors: {
    primaryPalette: PRIMARY_PALETTE,

    mediumPrimary:      PRIMARY_PALETTE['400'], // Medium Blue (brand)
    primary:      PRIMARY_PALETTE['600'], // Primary — Blue (brand)
    primaryHover: PRIMARY_PALETTE['700'], // Primary Hover — Dark Blue (brand)
    primaryMuted: PRIMARY_PALETTE['100'], // Primary Muted — Light Blue Tint

    background:      '#FFFFFF', // Background — White (light theme)
    surface:         '#FFFFFF', // Surface — White card / panel
    surfaceElevated: '#F1F5FB', // Elevated surface — soft blue-tinted

    text:      TEXT_COLOR,       // Primary text — Deep Navy (brand) on light
    textMuted: TEXT_MUTED_COLOR, // Text Muted — darker slate for stronger readability

    border: BORDER_COLOR, // Border — subtle light blue-grey

    success:  '#14B8A6', // Success — Teal (brand)
    warning:  '#F59E0B', // Warning — Orange (brand)
    error:    '#EF4444', // Error — Red (brand)

    whatsapp: '#25D366', // WhatsApp — WhatsApp Green (brand)

    metaBadgeBg:    '#2E3339', // Meta partner badge — dark grey pill
    metaBadgeBorder: '#5A6167', // Meta partner badge — subtle border
    metaBadgeText:   '#CCCCCC', // Meta partner badge — light grey label
  },
  // Partner commission-ladder tiers — an ascending-warmth progression built
  // FROM the existing brand palette (not literal metal colors/purple), so the
  // top tier (Platinum) reads as "the house color" — mastery, not a foreign
  // accent bolted on. Bronze/Silver are muted entry tones; Gold reuses the
  // brand's warning-amber as an earned/achievement color; Platinum is the
  // brand's own primary blue.
  tierColors: TIER_COLORS,
  chartColors: {
    series1: PRIMARY_PALETTE['600'],
    series2: TIER_COLORS.bronze.accent,
    series3: TIER_COLORS.gold.accent,
    neutral: TIER_COLORS.silver.accent,
    gain:    PRIMARY_PALETTE['600'],
    loss:    TIER_COLORS.bronze.accent,
    grid:    BORDER_COLOR,
  },
  admin: ADMIN_THEME,
  siteUrl: 'https://chatsyncs.com', // No www — must match Google Search Console property
  developerUrl: DEVELOPER_URL,
  helpUrl: HELP_URL,
  // One guide per feature-page slug. Learn guides are Help Center pages (meta_bot
  // tutorials/lib/docs/routes.ts, checked 2026-10-15); the three developer-api
  // entries are on the developer site. None are guessed.
  docsLinks: {
    'whatsapp-coexistence':   `${HELP_URL}/docs/whatsapp-coexistence-business-app`,
    'whatsapp-broadcast':     `${HELP_URL}/docs/send-bulk-whatsapp-messages`,
    'whatsapp-chatbot':       `${HELP_URL}/docs/whatsapp-keyword-chatbot`,
    'ai-agent':               `${HELP_URL}/docs/ai-agents`,
    'shared-inbox':           `${HELP_URL}/docs/shared-inbox`,
    'drip-campaigns':         `${HELP_URL}/docs/whatsapp-follow-up-sequences`,
    'whatsapp-catalog':       `${DEVELOPER_URL}/developer-api/catalog/list-catalogs`,
    'whatsapp-payments':      `${DEVELOPER_URL}/developer-api/catalog/update-order-status`,
    'appointment-booking':    `${HELP_URL}/docs/use-cases/clinic-appointment-booking-setup`,
    'whatsapp-forms':         `${HELP_URL}/docs/user-input-flow`,
    // The three feature pages added 2026-08-01
    'webhook-workflow':       `${HELP_URL}/docs/webhook-workflows`,
    'rest-api':               `${DEVELOPER_URL}/developer-api/introduction`,
    'incoming-outgoing-webhooks': `${HELP_URL}/docs/webhooks`,
  },
  appUrl: 'https://app.chatsyncs.com/',
  // appLoginUrl: 'https://app.chatsyncs.com/login',
  // appSignupUrl: 'https://app.chatsyncs.com/signup',
  appLoginUrl: 'https://platform.chatsyncs.com/login',
  appSignupUrl: 'https://platform.chatsyncs.com/register',
  appAccountUrl: 'https://platform.chatsyncs.com/settings/account',
  termsOfServiceUrl: '/terms-of-service',
  privacyPolicyUrl:  '/privacy-policy',
  refundPolicyUrl:   '/refund-policy',
  supportEmail:      SUPPORT_EMAIL,
  supportUrl:        `mailto:${SUPPORT_EMAIL}`,
  salesEmail:        SALES_EMAIL,
  salesUrl:          `mailto:${SALES_EMAIL}`,
  pricingFormWebhookUrl: 'https://n8n.srv894857.hstgr.cloud/webhook/chatsyncs-form',
  calComUrl: 'https://cal.com/lakshit-ukani-77/fix-your-lead-conversion-system',
  partnerProgramCalUrl: 'https://cal.com/chatsyncs/partner-program-enquiry',
  pricing: {
    starterMonthlyINR: 1699,
    proMonthlyINR: 3299,
    additionalWhatsAppNumberINR: 2299,
  },
  monthlyStarterPurchaseUrl: 'https://platform.chatsyncs.com/payment/buy-package/15714',
  monthlyProPurchaseUrl: 'https://platform.chatsyncs.com/payment/buy-package/15711',
  annualStarterPurchaseUrl: 'https://platform.chatsyncs.com/payment/buy-package/15713',
  annualProPurchaseUrl: 'https://platform.chatsyncs.com/payment/buy-package/15712',
  subscriptionUrl: 'https://www.chatsyncs.com/subscription',
}
